import { createReducer, on } from '@ngrx/store';
import { TeamSearchResults } from '../models/team-member';
import {
  clearDeleteTeamMemberError,
  loadingRoles,
  loadingTeam,
  savedTeamMember,
  storeDeleteTeamMemberError,
  storeRoles,
  storeRolesError,
  storeSavingTeamMember,
  storeSavingTeamMemberError,
  storeTeam,
  storeTeamError,
} from './team-management.actions';
import { Role } from '@team-management/models/roles';

export const featureKey = 'team-management';

interface EntityTeamState {
  client: TeamSearchResults;
  wcg: TeamSearchResults;
}

export interface TeamManagementState {
  deleteTeamMemberError: {
    [memberId: string]: string | null;
  };
  roles: {
    site: Role[];
    study: Role[];
  };
  loadingRoles: {
    site: boolean;
    study: boolean;
  };
  rolesError: {
    site: string | null;
    study: string | null;
  };
  teams: {
    [studyId: string]: {
      site: EntityTeamState;
      study: EntityTeamState;
    };
  };
  loading: {
    [studyId: string]: {
      site: {
        client: boolean;
        wcg: boolean;
      };
      study: {
        client: boolean;
        wcg: boolean;
      };
    };
  };
  errors: {
    [studyId: string]: {
      site: {
        client: string | null;
        wcg: string | null;
      };
      study: {
        client: string | null;
        wcg: string | null;
      };
    };
  };
  savingTeamMember: boolean;
  savingTeamMemberError: string | null;
}

export const initialState: TeamManagementState = {
  deleteTeamMemberError: {},
  roles: {
    site: [],
    study: [],
  },
  loadingRoles: {
    site: false,
    study: false,
  },
  rolesError: {
    site: null,
    study: null,
  },
  teams: {},
  loading: {},
  errors: {},
  savingTeamMember: false,
  savingTeamMemberError: null,
};

export const teamManagementReducer = createReducer(
  initialState,
  on(clearDeleteTeamMemberError, (state, { memberId }) => {
    const errors = { ...state.deleteTeamMemberError };

    delete errors[memberId];

    return {
      ...state,
      deleteTeamMemberError: errors,
    };
  }),
  on(storeDeleteTeamMemberError, (state, { memberId, error }) => ({
    ...state,
    deleteTeamMemberError: {
      ...state.deleteTeamMemberError,
      [memberId]: error,
    },
  })),
  on(storeRoles, (state, { roles, level }) => ({
    ...state,
    roles: {
      ...state.roles,
      [level]: roles,
    },
    loadingRoles: {
      ...state.loadingRoles,
      [level]: false,
    },
    rolesError: {
      ...state.rolesError,
      [level]: null,
    },
  })),
  on(storeRolesError, (state, { error, level }) => ({
    ...state,
    loadingRoles: {
      ...state.loadingRoles,
      [level]: false,
    },
    rolesError: {
      ...state.rolesError,
      [level]: error,
    },
  })),
  on(loadingRoles, (state, { level }) => ({
    ...state,
    loadingRoles: {
      ...state.loadingRoles,
      [level]: true,
    },
    rolesError: {
      ...state.rolesError,
      [level]: null,
    },
  })),
  on(
    storeTeam,
    (
      state,
      {
        entityId,
        team,
        memberType,
        pageSize,
        page,
        total,
        level,
        roles,
        status,
        visible,
      },
    ) => ({
      ...state,
      teams: {
        ...state.teams,
        [entityId]: {
          ...(state.teams[entityId] || {}),
          [level]: {
            ...(state.teams?.[entityId]?.[level] || {}),
            [memberType]: {
              ...(state.teams[entityId]?.[level]?.[memberType] || {}),
              team,
              page,
              pageSize,
              total,
              roles,
              status,
              visible,
            },
          },
        },
      },
      loading: {
        ...state.loading,
        [entityId]: {
          ...state.loading[entityId],
          [level]: {
            ...(state.loading[entityId]?.[level] || {}),
            [memberType]: false,
          },
        },
      },
      errors: {
        ...state.errors,
        [entityId]: {
          ...state.errors[entityId],
          [memberType]: null,
        },
      },
    }),
  ),
  on(loadingTeam, (state, { entityId, memberType, level }) => ({
    ...state,
    loading: {
      ...state.loading,
      [entityId]: {
        ...(state.loading[entityId] || {}),
        [level]: {
          ...(state.loading[entityId]?.[level] || {}),
          [memberType]: true,
        },
      },
    },
  })),
  on(storeTeamError, (state, { entityId, memberType, error, level }) => ({
    ...state,
    errors: {
      ...state.errors,
      [entityId]: {
        ...state.errors[entityId],
        [level]: {
          ...state.errors[entityId]?.[level],
          [memberType]: error,
        },
      },
    },
    loading: {
      ...state.loading,
      [entityId]: {
        ...(state.loading[entityId] || {}),
        [level]: {
          ...(state.loading[entityId]?.[level] || {}),
          [memberType]: false,
        },
      },
    },
  })),
  on(storeSavingTeamMember, (state) => ({
    ...state,
    savingTeamMember: true,
    savingTeamMemberError: null,
  })),
  on(storeSavingTeamMemberError, (state, { error }) => ({
    ...state,
    savingTeamMember: false,
    savingTeamMemberError: error,
  })),
  on(savedTeamMember, (state) => ({
    ...state,
    savingTeamMember: false,
    savingTeamMemberError: null,
  })),
);
